/**
 * @packageDocumentation
 * @module @yimoka/web-http/create-web-http-client
 * @summary 创建基于 axios 的 Web HTTP 客户端与 apiExecutor
 * @author keep
 * @since 0.0.1
 */

import { IAPIRequestConfig, IHTTPResponse } from '@yimoka/shared';
import type { IAPIExecutor } from '@yimoka/store';
import axios, { type InternalAxiosRequestConfig } from 'axios';

import { handleAxiosError, handleAxiosResponse } from './handle-axios-response';
import type { IWebHttpClient, IWebHttpClientOptions } from './types';

const DEFAULT_TIMEOUT = 10_000;

type AxiosHeadersLike = InternalAxiosRequestConfig['headers'];

/**
 * 当 header 不存在时才注入，避免覆盖单次请求 / defaults 中已有的值。
 */
function setIfAbsent(headers: AxiosHeadersLike, key: string, value?: string | null): void {
  if (value === undefined || value === null || value === '') return;
  if (headers.has(key)) return;
  headers.set(key, value);
}

function applyDefaultHeaders(headers: AxiosHeadersLike, defaultHeaders?: IWebHttpClientOptions['headers']): void {
  setIfAbsent(headers, 'Content-Type', 'application/json');
  if (!defaultHeaders) return;
  for (const [key, value] of Object.entries(defaultHeaders)) {
    setIfAbsent(headers, key, value);
  }
}

function applyBuiltinHeaders(
  headers: AxiosHeadersLike,
  channel: string,
  useType: string,
  platform: string,
): void {
  setIfAbsent(headers, 'x-md-global-channel', channel);
  setIfAbsent(headers, 'x-md-global-use-type', useType);
  setIfAbsent(headers, 'x-md-global-platform', platform);
}

function readHeaderValue(getter?: () => string | undefined | null): string | undefined {
  return getter?.() ?? undefined;
}

function applyDynamicHeaders(headers: AxiosHeadersLike, options: IWebHttpClientOptions): void {
  const dynamicHeaders: Array<[string, string | undefined]> = [
    ['Authorization', readHeaderValue(options.getAuthorization)],
    ['x-md-global-client-id', readHeaderValue(options.getClientID)],
    ['x-md-global-tenantID', readHeaderValue(options.getTenantID)],
    ['x-md-global-language', readHeaderValue(options.getLanguage)],
    ['x-md-global-app', options.app ?? readHeaderValue(options.getApp)],
  ];

  for (const [key, value] of dynamicHeaders) {
    setIfAbsent(headers, key, value);
  }
}

/**
 * 创建 Web HTTP 客户端，返回 {@link IAPIExecutor} 与底层 axios 实例。
 *
 * @param options - 客户端配置
 * @returns 客户端实例
 *
 * @example
 * ```ts
 * // 最简：仅 baseURL
 * const { apiExecutor } = createWebHttpClient({ baseURL: 'https://api.example.com' });
 *
 * // 带默认头 + 动态 token
 * const client = createWebHttpClient({
 *   baseURL: 'https://api.example.com',
 *   headers: { 'x-md-global-language': 'zh' },
 *   getAuthorization: () => token,
 *   getTenantID: () => tenantId,
 * });
 *
 * // 复用已有 axios 实例（本包不再注入拦截器）
 * const client = createWebHttpClient({ axiosInstance: customAxios });
 * ```
 */
export function createWebHttpClient(options: IWebHttpClientOptions): IWebHttpClient {
  const {
    baseURL,
    channel = 'web',
    useType = 'client',
    platform = 'web',
    app: appOption,
    headers: defaultHeaders,
    timeout = DEFAULT_TIMEOUT,
    axiosInstance,
    axiosConfig,
    getAuthorization,
    getClientID,
    getTenantID,
    getLanguage,
    getApp,
  } = options;

  const instance = axiosInstance ?? axios.create({
    baseURL,
    timeout,
    ...(axiosConfig ?? {}),
  });

  // 用户传入自定义 axios 实例时，由其自行管理 header / 拦截器，本包不介入
  if (!axiosInstance) {
    instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const { headers } = config;
      applyDefaultHeaders(headers, defaultHeaders);
      applyBuiltinHeaders(headers, channel, useType, platform);
      applyDynamicHeaders(headers, {
        app: appOption,
        getApp,
        getAuthorization,
        getClientID,
        getLanguage,
        getTenantID,
      });

      return config;
    });
  }

  const apiExecutor: IAPIExecutor = async <T extends object = IAPIRequestConfig>(
    config: T,
  ): Promise<IHTTPResponse> => {
    const req = config as IAPIRequestConfig;
    try {
      const response = await instance.request({
        ...req,
        method: (req.method ?? 'GET') as string,
      });
      return handleAxiosResponse(response);
    } catch (error: unknown) {
      return handleAxiosError(error);
    }
  };

  return {
    instance,
    apiExecutor,
  };
}
